/**
 * Chooses the REPL's agent runtime: gateway-backed when logged in (the backend
 * runs the agent loop; this client only executes the tools it requests), else the
 * local direct-to-DeepSeek loop as an offline fallback. Both satisfy the same
 * {@link ReplAgent} interface, so the REPL is agnostic.
 *
 * The gateway runtime keeps the conversation history client-side and replays it
 * each turn; the backend owns the system prefix, context gathering (agentic tool
 * reads), memory, and the session digest. Either way the turn is driven to
 * ACTUAL completion by finish.ts: auto-continue on a length cut, verify after
 * writes, bounded repair rounds — the user never has to type "continue".
 */
import type { AiUsage } from '@fim/ai-sdk';
import type { Message } from '@fim/futurex-protocol';
import { readCreds } from './creds.js';
import { createGatewayClient } from './client/gateway.js';
import { createChangeTracker } from './change-significance.js';
import { createReplAgent, type AgentOptions, type Gate, type ReplAgent } from './agent.js';
import { createToolRunner } from './tool-runner.js';
import { runGatewayTurnToCompletion } from './finish.js';
import { error } from './ui.js';
import type { SessionContext } from './context.js';
import type { CliOptions } from './options.js';

export function createReplRuntime(
  ctx: SessionContext,
  opts: CliOptions,
  agentOptions: AgentOptions,
  gate: Gate,
): ReplAgent {
  const creds = readCreds();

  // Not logged in → local fallback (direct DeepSeek). `futurex login` to use the API.
  if (!creds?.accessToken) {
    return createReplAgent(ctx, opts, agentOptions, gate);
  }

  const tracker = createChangeTracker();
  const client = createGatewayClient(creds, {
    gate,
    repoRoot: ctx.root,
    onTool: (t) => tracker.observe(t),
    ...(opts.useRepo !== undefined ? { useRepo: opts.useRepo } : {}),
    ...(opts.useMemory !== undefined ? { useMemory: opts.useMemory } : {}),
  });
  const history: Message[] = [];
  const toolCount = createToolRunner(ctx.root).count;

  return {
    toolCount,
    async turn(userMessage: string, model: string, signal: AbortSignal): Promise<AiUsage | undefined> {
      history.push({ role: 'user', content: userMessage });
      const result = await runGatewayTurnToCompletion({
        client,
        history,
        base: { model },
        signal,
        root: ctx.root,
        verify: agentOptions.verify ?? 'auto',
        mutated: () => tracker.mutated(),
        changes: () => tracker.changes(),
        resetMutated: () => tracker.reset(),
      });
      if (result.error && !signal.aborted) error(result.error);
      return result.usage;
    },
    clear() {
      history.length = 0;
    },
    size() {
      return history.length;
    },
  };
}
